/**
 * VRAM accountant (rendering plan §2.3). `renderer.info.memory.texturesSize` is WORTHLESS for
 * this project: r185 accounts every compressed texture as exactly 1 byte
 * (`_getTextureMemorySize` returns `1` for `isCompressedTexture`), and our whole texture budget
 * is KTX2-compressed. So we keep our own ledger: texture GPU size = sum of mip byteLengths,
 * charged to an owning object (a cell, the character, …) and credited back on eviction.
 *
 * Pure-ish by design (duck-typed texture objects, no three import) so it unit-tests headlessly
 * and never drags the renderer into the accounting path.
 */

/** The texture surface sizeOf understands — matches THREE.Texture, fabricable in tests. */
export interface TextureLike {
  /** three sets this on every Texture. The census duck-types on it — see `texturesOf` below. */
  isTexture?: boolean;
  isCompressedTexture?: boolean;
  isData3DTexture?: boolean;
  /** CompressedTexture / DataTexture mip chain (data may be absent on regular textures). */
  mipmaps?: Array<{ data?: { byteLength: number }; width?: number; height?: number }>;
  /** DataTexture payload, or an upload image with dimensions. */
  image?: { data?: { byteLength: number }; width?: number; height?: number; depth?: number };
  generateMipmaps?: boolean;
}

/**
 * Estimated GPU bytes for one texture:
 * - compressed / data textures with a mip chain → exact sum of mip `byteLength`s;
 * - DataTexture (single level) → `image.data.byteLength` (+1/3 if mips are generated);
 * - regular image textures → width × height (× depth) × 4 bytes RGBA8, ×4/3 for mips.
 * Unknown shapes → 0 (never throws; the accountant must not crash rendering).
 */
export function sizeOf(tex: TextureLike): number {
  const mips = tex.mipmaps;
  if (mips && mips.length > 0 && mips.every((m) => m.data !== undefined)) {
    return mips.reduce((n, m) => n + (m.data?.byteLength ?? 0), 0);
  }
  const img = tex.image;
  if (img?.data !== undefined) {
    const base = img.data.byteLength;
    return tex.generateMipmaps === true ? Math.ceil((base * 4) / 3) : base;
  }
  if (img && typeof img.width === 'number' && typeof img.height === 'number') {
    const base = img.width * img.height * (img.depth ?? 1) * 4;
    return tex.generateMipmaps === false ? base : Math.ceil((base * 4) / 3);
  }
  return 0;
}

interface Ledger {
  bytes: number;
  textures: Set<TextureLike>;
}

/**
 * WeakMap ledger: charges attach to an owner object; when the owner is credited (evicted) its
 * whole charge is released. The same texture charged twice to one owner counts once (uuid-free
 * identity — the Set is by object reference). If an owner is GC'd without a credit, its bytes
 * stay in the grand total — always credit on disposal (assert-tested at the cell loader, R2).
 */
export class VramAccountant {
  private readonly ledgers = new WeakMap<object, Ledger>();
  private total = 0;

  /** Charge one texture to `owner`. Re-charging the same texture to the same owner is a no-op. */
  charge(owner: object, tex: TextureLike): void {
    let ledger = this.ledgers.get(owner);
    if (!ledger) {
      ledger = { bytes: 0, textures: new Set() };
      this.ledgers.set(owner, ledger);
    }
    if (ledger.textures.has(tex)) return;
    ledger.textures.add(tex);
    const bytes = sizeOf(tex);
    ledger.bytes += bytes;
    this.total += bytes;
  }

  /** Release everything charged to `owner` (cell eviction, character unload). */
  credit(owner: object): void {
    const ledger = this.ledgers.get(owner);
    if (!ledger) return;
    this.total -= ledger.bytes;
    this.ledgers.delete(owner);
  }

  /** Bytes currently charged to `owner`. */
  totalFor(owner: object): number {
    return this.ledgers.get(owner)?.bytes ?? 0;
  }

  /** Bytes across every live owner. O(1) — safe to read every frame for the panel. */
  grandTotal(): number {
    return this.total;
  }
}

/* ────────────────────────────────────────────────────────────────────────────────────────────
 * SCENE CENSUS (R1.5) — WHY THE LEDGER ALONE WAS REPORTING A CONFIDENT ZERO
 *
 * The ledger above is an ACCRUAL model: something charges, something credits. That is the right
 * shape for eviction (a streamed cell, the character) and it is what R2's budget enforcement will
 * hang off. But it can only ever see what someone remembered to charge, and exactly one caller
 * ever did — the character loader in `src/presentation/scene.ts`. Everything that arrives through
 * `GLTFLoader` on the world path (`static_mesh` decor, i.e. every asset that matters) charged
 * nothing, so `getVramMB()` reported **0.0 MB for Sponza's 68 × 1024² textures** while the
 * rendering plan's memory budget was nominally being enforced with that number. A budget enforced
 * with a blind instrument is not enforced.
 *
 * The fix is a second, complementary model rather than a hundred new charge calls: a CENSUS that
 * walks the live scene graph and prices what is actually resident. Making every loader charge the
 * accountant would have pushed a renderer concern into `src/entities/**` (which reaches the world
 * only through `EntityCtx` by design) and would have failed the same way again the first time
 * somebody added a loader and forgot. A census cannot be forgotten: if it is in the scene, it is
 * counted.
 *
 * ⚠️ HONESTLY INCOMPLETE, AND IT SAYS SO. `VramCensus` reports what it can see and NAMES what it
 * cannot, because a confident wrong total is what got us here. Counted: every distinct texture
 * bound to a material of a mesh in the graph (discovered by duck-typing `isTexture`, so a slot
 * nobody thought of is still found), plus geometry attribute and index buffers. NOT counted, and
 * reported as such:
 *   - RENDER TARGETS — the post chain's, and any target a system holds off-graph. ⚠️ SHADOW MAPS
 *     LEFT THIS LIST IN L2: they were the largest item on it (~33 MB for the sun's 2048² alone,
 *     because three's WebGPU path allocates a full RGBA colour attachment beside every depth
 *     texture) and they are now MEASURED from `light.shadow.mapSize` — see `shadowMapBytes`. An
 *     entry leaves this list by being closed, never by being forgotten.
 *   - GPU-SIDE COMPRESSION. A texture with no mip chain is priced as RGBA8 (`sizeOf`), which is
 *     right for today's uncompressed glTF and will OVER-count once A4's KTX2 cook lands. It is
 *     over-counting on the safe side; the mip-chain branch prices KTX2 exactly.
 *   - Anything off-graph: pooled/preloaded assets not yet added, and the ledger's own owners if
 *     they are not in the scene. `grandTotal()` still reports those separately.
 * ──────────────────────────────────────────────────────────────────────────────────────────── */

/** Minimal Object3D surface the census walks — duck-typed so this module still imports no three. */
export interface Object3DLike {
  traverse(cb: (o: Object3DLike) => void): void;
  /** Light surface, for the shadow-map half of the bill (L2). Absent on everything else. */
  isLight?: boolean;
  isPointLight?: boolean;
  castShadow?: boolean;
  shadow?: { mapSize?: { width?: number; height?: number } } | null;
  /** Stamped by `markOverlay` on dev-only debug overlays — see `DEBUG_OVERLAY_USERDATA`. */
  userData?: Record<string, unknown>;
  isMesh?: boolean;
  geometry?: GeometryLike;
  material?: unknown;
  background?: unknown;
  environment?: unknown;
}

/** Minimal BufferGeometry surface: attribute + index buffers are the geometry half of the bill. */
export interface GeometryLike {
  uuid?: string;
  attributes?: Record<string, { array?: { byteLength?: number } } | undefined>;
  index?: { array?: { byteLength?: number } } | null;
}

export interface VramCensus {
  /** Bytes of distinct textures reachable from the scene graph. */
  textureBytes: number;
  /** Bytes of distinct geometry attribute + index buffers. */
  geometryBytes: number;
  /** Distinct textures counted. */
  textures: number;
  /** Distinct geometries counted. */
  geometries: number;
  /**
   * ── ⭐ L2 · SHADOW MAPS, WHICH THIS CENSUS SPENT ITS WHOLE LIFE DECLARING IT COULD NOT SEE ──
   *
   * `excludes` carried `'shadow-maps'` from the day it was written, and R1.5-E measured what that
   * was worth: **~33 MB for the sun's 2048² alone**, omitted by an instrument that priced the
   * geometry beside it to the byte. It is arithmetic over `shadow.mapSize`, so there was never a
   * reason for it to be a hole — only nobody had written the multiply.
   *
   * 8 B/texel, from the r185 allocation rather than a rule of thumb: `ShadowNode.setupRenderTarget`
   * makes a `DepthTexture` (UnsignedInt, 4 B) AND a full RGBA colour attachment beside it
   * (`shadowMap.texture.type = shadow.mapType`, `UnsignedByteType`, 4 B). ×6 for a point light,
   * whose `PointShadowNode` allocates a CUBE target.
   *
   * ⚠️ IT PRICES `mapSize`, NOT AN ALLOCATION, and the direction of that error is chosen. The
   * target is created lazily on the light's first render, so this over-states on frame 0 by
   * exactly the maps that are about to exist. The failure that has actually bitten this repo is
   * the other one — the accrual ledger reporting 0.0 MB for a 68-texture scene — so the census
   * prices what WILL be resident.
   */
  shadowMapBytes: number;
  /** Shadow-casting lights found in the graph. A point light is ONE map of six faces, not six. */
  shadowMaps: number;
  /**
   * Textures the census FOUND but could not price (`sizeOf` returned 0 — no mip chain, no data,
   * no image dimensions). The honest-incompleteness signal: a non-zero value here means the total
   * is a floor, not a total.
   */
  unpricedTextures: number;
  /**
   * What this census structurally cannot see, named so a reader of the number knows its shape.
   * Constant today; it is a field rather than a comment so it reaches the dev hook and the panel.
   */
  readonly excludes: readonly string[];
}

const CENSUS_EXCLUDES: readonly string[] = [
  'render-targets',
  // 'shadow-maps' was here until L2. It is now MEASURED (`shadowMapBytes` above) rather than
  // declared unmeasurable, which is the whole point of this list existing: an entry leaves it by
  // being closed, never by being forgotten. The remaining two are still real holes.
  'off-graph-assets',
  // ⭐ E1 + E3: dev-only furniture, stamped by `markOverlay` and skipped below — the debug view's
  // overlays (probe volumes, brick bounds) AND the editor's own gizmos (light and GI-volume
  // billboards, the selection box, the TransformControls helper, the drag preview). Named here
  // rather than merely skipped, because a census that silently omits things is worse than one that
  // over-charges: the number stops being checkable, and `excludes` exists precisely so a reader
  // knows the shape of what it cannot see.
  //
  // ⚠️ E3 measured this: before the editor stamped its own roots, pressing F2 on Sponza moved
  // `getVramBreakdown()`. A budget instrument whose reading changes when you open a panel is not a
  // budget instrument.
  'debug-overlays',
];

/**
 * ⭐ THE `userData` KEY THAT KEEPS A DEBUG OVERLAY OFF THE VRAM BILL.
 *
 * It lives HERE, in the module that must skip them, rather than in `render/debugView/` — that
 * directory is DEV-ONLY and must be ABSENT from a production build (`checkBundle`), and this
 * module is EAGER and ships. An import the other way round would drag the whole debug table into
 * the main chunk; `debugView` imports this constant instead, so there is still exactly one
 * definition of the name.
 */
export const DEBUG_OVERLAY_USERDATA = 'fwgDebugOverlay';

/**
 * ── ⭐ `markOverlay` — THE ONE DEFINITION OF "THIS OBJECT IS DEV FURNITURE" (E1 · E3) ────────────
 *
 * It does two things at once because they are the same claim about the same object:
 *
 *  1. **It stays off the VRAM bill** — `DEBUG_OVERLAY_USERDATA`, which `censusScene` above skips
 *     and `CENSUS_EXCLUDES` declares.
 *  2. **No view mode may repaint it** — `material.allowOverride = false`. `scene.overrideMaterial`
 *     hits EVERYTHING whose material allows it (r185 `Material.js:438`, applied in
 *     `Renderer.renderObject`), so without this, entering Overdraw repaints the transform gizmo
 *     and entering Indirection Level paints the sky dome a false colour — which is not merely ugly,
 *     it is a WRONG ANSWER: the sky is not lit by a brick, and a light's billboard is not a
 *     surface at all.
 *
 * ⚠️ IT LIVES HERE, IN THE SHIPPING MODULE, AND THAT IS AN E3 MOVE WITH A REASON.
 *
 * It was born in `render/debugView/overlay.ts`, which is DEV-ONLY at the ABSENT bar. The editor's
 * own gizmos (light billboards, GI-volume billboards, the selection box, the TransformControls
 * helper) need exactly the same two properties — they are scene-graph furniture under
 * `editorRoot`, and `censusScene` was charging an artist for them — but `src/editor/**` MUST NOT
 * import `render/debugView/**` (the `editor-*.js` chunk IS emitted in a production build, so that
 * import would carry the whole debug table and its shader graphs into `dist/`; `checkBundle` (c6)
 * is what says so). Two copies of this six-line walk would be two answers to "is this object
 * furniture", and the failure mode is silent: a gizmo stamped by one copy and missed by the other
 * shows up as a VRAM number that moves when you open a panel.
 *
 * So it lives beside the key it writes, in the module that must skip it, which both sides may
 * import. `debugView/overlay.ts` re-exports it so its own readers are unchanged.
 *
 * The `childadded` hook is the same pattern (and the same reason) as `scene.ts`'s `optInSubtree`:
 * these subtrees GROW after the mark — an overlay rebuilds its lines on every `sync()`, the editor
 * rebuilds its billboards whenever the light list changes — and a one-shot walk would silently
 * stop covering them. Idempotent: `fwgOverlayHook` guards against stacking listeners.
 */
export interface MarkableObject3D extends Object3DLike {
  addEventListener?(type: 'childadded', cb: (e: { child: unknown }) => void): void;
}

export function markOverlay(root: MarkableObject3D): void {
  root.traverse((raw) => {
    const o = raw as MarkableObject3D & { material?: unknown };
    o.userData ??= {};
    o.userData[DEBUG_OVERLAY_USERDATA] = true;
    const mat = o.material as { allowOverride?: boolean } | { allowOverride?: boolean }[] | undefined;
    if (Array.isArray(mat)) for (const m of mat) m.allowOverride = false;
    else if (mat) mat.allowOverride = false;
    if (o.userData['fwgOverlayHook'] !== true) {
      o.userData['fwgOverlayHook'] = true;
      o.addEventListener?.('childadded', (e) => markOverlay(e.child as MarkableObject3D));
    }
  });
}

/**
 * Bytes one shadow-casting light's map costs. Duplicated from `lightModel.shadowMapBytes` on
 * purpose and it is a THIRTY-CHARACTER duplication with a fence, not a second answer: this module
 * is EAGER and ships in every bundle, `lightModel.ts` is pure and imports nothing, and an import
 * either way round would put one of them on the other's dependency chain — `world/schema.ts`
 * imports `lightModel`, and `tools/bundler/bake-world.ts` imports `world/schema.ts` and runs
 * UNBUNDLED under `node --experimental-strip-types`. `tests/vramAccountant.test.ts` asserts the two
 * agree to the byte over the whole (type × size) matrix, which is the only property that matters.
 *
 * It reads the GRAPH — `light.castShadow` and `light.shadow.mapSize` — rather than the config,
 * exactly like the rest of this walk. `censusScene` was made a graph walk instead of a ledger
 * because a ledger reported 0.0 MB for a 68-texture scene, and asking the config here would
 * re-introduce the same class of blindness one field at a time.
 */
/*
 * The ONE name for "the shadow maps this light's rig drives". `lightModel.ts` imports NOTHING (it
 * is on the unbundled baker's chain and that is a hard constraint of its own), so it costs this
 * module none of its "no three, no DOM" character to read a string constant from it.
 */
import { FWG_DRIVEN_SHADOW_MAPS } from '../lights/lightModel';

const SHADOW_MAP_BYTES_PER_TEXEL = 8;

interface ShadowMapLike {
  mapSize?: { width?: number; height?: number };
}

interface ShadowedLightLike {
  isLight?: boolean;
  isPointLight?: boolean;
  castShadow?: boolean;
  shadow?:
    | (ShadowMapLike & {
        /** B3: a `CSMShadowNode` attached to the light — its `lights[]` carry the real maps. */
        shadowNode?: { lights?: Array<{ shadow?: ShadowMapLike }> } | undefined;
        /**
         * ⭐ WHAT THE RIG SAYS IT DRIVES (`lightModel.FWG_DRIVEN_SHADOW_MAPS`). Preferred over the
         * reach below, because the slot holds a COMPOSITION whenever the baked sun term or the
         * two-map split is installed — and a composition has no `.lights`. See that constant.
         */
        [FWG_DRIVEN_SHADOW_MAPS]?: ShadowMapLike[] | undefined;
      })
    | null;
}

const mapBytes = (shadow: ShadowMapLike | undefined | null, faces: number): number => {
  const w = shadow?.mapSize?.width ?? 0;
  const h = shadow?.mapSize?.height ?? 0;
  if (w <= 0 || h <= 0) return 0;
  return w * h * SHADOW_MAP_BYTES_PER_TEXEL * faces;
};

function shadowBytesOf(o: unknown): number {
  const light = o as ShadowedLightLike;
  if (light.isLight !== true || light.castShadow !== true) return 0;
  /*
   * B3 · CASCADES. With a `CSMShadowNode` on `shadow.shadowNode` the light's OWN map is never
   * allocated (`AnalyticLightNode.setupShadow` takes the custom node instead of
   * `setupShadowNode()`), and the maps that ARE allocated belong to the node's cascade objects —
   * plain `Object3D`s this walk would never see as lights. So the sun under cascades is priced as
   * the sum of its cascades and nothing for itself; `lightModel.sunShadowPolicy().bytes` is the
   * same arithmetic from the other side, and `tests/renderCsm.test.ts` holds the two to the byte.
   */
  /*
   * ⭐ ASK THE RIG FIRST. `shadow.shadowNode.lights` is a duck-type into three's `CSMShadowNode`,
   * and it only works while that node sits in the slot BARE — which it does not whenever the baked
   * per-probe sun term or the two-map split is installed, both of which put a composition there.
   * `csmRig` publishes the maps it actually drives (`FWG_DRIVEN_SHADOW_MAPS`) precisely so this
   * question has an answer that does not depend on the shape of a vendor node.
   */
  const driven = light.shadow?.[FWG_DRIVEN_SHADOW_MAPS];
  if (Array.isArray(driven) && driven.length > 0) {
    return driven.reduce((sum, s) => sum + mapBytes(s, 1), 0);
  }
  const cascades = light.shadow?.shadowNode?.lights;
  if (Array.isArray(cascades) && cascades.length > 0) {
    return cascades.reduce((sum, lw) => sum + mapBytes(lw.shadow, 1), 0);
  }
  // A point light's shadow is a cube: `PointShadowNode` calls `createCubeRenderTarget( size )` and
  // `renderShadow` loops `for ( let face = 0; face < 6; face ++ )`. Six faces of memory, and six
  // full scene renders per refresh — which is why `shadowTraversals` prices it at 6 too.
  return mapBytes(light.shadow, light.isPointLight === true ? 6 : 1);
}

/** Everything on `material` that quacks like a texture — slot names are not enumerated on purpose. */
function texturesOf(material: unknown, out: Set<TextureLike>): void {
  if (material === null || typeof material !== 'object') return;
  for (const value of Object.values(material as Record<string, unknown>)) {
    if (value !== null && typeof value === 'object' && (value as { isTexture?: boolean }).isTexture === true) {
      out.add(value as TextureLike);
    }
  }
}

function geometryBytes(geo: GeometryLike): number {
  let bytes = 0;
  for (const attr of Object.values(geo.attributes ?? {})) {
    bytes += attr?.array?.byteLength ?? 0;
  }
  bytes += geo.index?.array?.byteLength ?? 0;
  return bytes;
}

/**
 * Price everything reachable from `root`. Distinct by OBJECT IDENTITY (a texture shared by ten
 * materials is one upload, and counting it ten times is how a ledger convinces you to evict the
 * wrong thing). Never throws — a malformed object contributes zero, exactly like `sizeOf`.
 */
export function censusScene(root: Object3DLike | null | undefined): VramCensus {
  const textures = new Set<TextureLike>();
  const geometries = new Set<GeometryLike>();
  let shadowBytes = 0;
  let shadowMaps = 0;
  if (root) {
    // Scene-level image-based lighting lives beside the graph, not in it.
    texturesOf({ background: root.background, environment: root.environment }, textures);
    root.traverse((o) => {
      /*
       * Sponza's `brickBounds` overlay is 116 boxes of merged line geometry. Charging an artist
       * for it would make the VRAM readout move when a DIAGNOSTIC is switched on, which is the
       * shape of an instrument that changes what it measures. `markOverlay` stamps every object
       * it builds AND hooks `childadded`, so a per-object test here is a subtree skip in effect.
       */
      if (o.userData?.[DEBUG_OVERLAY_USERDATA] === true) return;
      const shadow = shadowBytesOf(o);
      if (shadow > 0) {
        shadowBytes += shadow;
        shadowMaps++;
      }
      if (o.geometry) geometries.add(o.geometry);
      const mat = o.material;
      if (Array.isArray(mat)) for (const m of mat) texturesOf(m, textures);
      else texturesOf(mat, textures);
    });
  }
  let textureBytes = 0;
  let unpriced = 0;
  for (const tex of textures) {
    const bytes = sizeOf(tex);
    textureBytes += bytes;
    if (bytes === 0) unpriced++;
  }
  let geoBytes = 0;
  for (const geo of geometries) geoBytes += geometryBytes(geo);
  return {
    textureBytes,
    geometryBytes: geoBytes,
    textures: textures.size,
    geometries: geometries.size,
    shadowMapBytes: shadowBytes,
    shadowMaps,
    unpricedTextures: unpriced,
    excludes: CENSUS_EXCLUDES,
  };
}
